import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import CTA from 'Src/components/buttons/CTA';
import { useCheckDescribe } from 'Src/feature/check-util/useCheckDescribe';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useForm } from 'Src/feature/form/useForm';
import { useCheckExitPrompt } from 'Src/feature/modal/useCheckExitPrompt';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useCurrentPayment } from 'Src/feature/payment/useCurrentPayment';
import { usePrinter } from 'Src/feature/printer/usePrinter';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { useToast } from 'Src/feature/toasts/useToast';
import { useVoidReason } from 'Src/feature/void-reason/useVoidReason';
import useRoute from 'Src/routes/useRoute';

import { useCheck } from './useCheck';
import { useCheckPanelFooterPermissions } from './useCheckPanelFooterPermissions';

const sectionId = 'useCheckPanelFooter';

type ViewMode = 'CheckItemEdit' | 'CheckDetails' | 'CheckDetailsExpanded' | 'CheckDetailsMore';

const useCheckPanelFooter = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const toast = useToast();

  const printerSend = usePrinter.mutate.send();
  const printCheck = useCheck.mutate.print();
  const terminalSettings = useTerminal.query.settings();
  const currentPayment = useCurrentPayment();
  const voidReasonGetAll = useVoidReason.query.getAll();
  const updateStatus = useCheck.mutate.updateStatus();
  const clear = useCheck.mutate.clear();
  const removeCheckItems = useCheck.mutate.updateItemStatus();

  const [viewMode, viewModeSet] = useState(undefined as undefined | ViewMode);

  const checkExitPrompt = useCheckExitPrompt();

  const grantedPermissions = useCheckPanelFooterPermissions();

  const { crumbs, navigate, route } = useRoute({
    // will only fire when route changes happen
    onChange: (store) => {
      // is payment route
      if (store.route.is(store.crumbs.pos.check.payment.main)) {
        viewModeSet('CheckDetailsExpanded');
      } else {
        if (store.route.is(store.crumbs.pos.check.item.new) || store.route.is(store.crumbs.pos.check.item.edit) || store.route.params.active.itemSelect === 'many') {
          viewModeSet('CheckItemEdit');
        } else {
          viewModeSet(undefined);
        }
      }
    }
  });

  const checkId = route.params.active.checkId
    ? parseInt(route.params.active.checkId, 10)
    : undefined;
  const checkGetOne = useCheck.query.getOne(checkId);

  const described = useCheckDescribe({ check: checkGetOne.data });

  const modal = {
    discount: useModalControl(),
    voidCheck: useModalControl({
      onOpen: () => {
        voidCheckForm.action.reset();
      },
    }),
    clearCheck: useModalControl(),
    mergeCheck: useModalControl(),
    fees: useModalControl(),
  };

  const voidReasonOptions = (voidReasonGetAll.data?.items ?? []).map((vr) => ({ value: vr.id, label: vr.name }));

  const voidCheckForm = useForm<{ reasonId: number }>({
    defaultValues: {
      reasonId: undefined,
    },
    schema: object().shape({
      reasonId: number().label(t('voidReason.label')).required(),
    }),
  });

  const navigateToDefaultView = (args?: { isFromVoid?: boolean, isFromClear?: boolean }) => {
    const overrideNewCheckDefault = args ? (args.isFromVoid || args.isFromClear) : false;
    if (terminalSettings.data?.defaultView === 'NewCheck' && overrideNewCheckDefault) {
      navigate(crumbs.pos.checks.main.interpolate({ tabId: route.params.value.checksList }));
    } else {
      navigate(crumbs.pos.checks.main.interpolate({ tabId: route.params.value.defaultView }));
    }
  };

  const sendItems = (isManualSend: boolean = false) => {
    if (typeof checkId !== 'number') return log.debug('sendItems', '!checkId');
    if (described.unsent.check.itemIds.length) {
      // Send Kitchen Print Request to pos-api for all unsent items
      // Individual check item statuses will get updated by pos-api after printing.
      // The last arg in the following api call is set to 0 so that the check will print
      // immediately, as opposed to with a delay.
      printCheck.mutate({
        checkId,
        itemIds: described.unsent.check.itemIds,
        delayMins: 0,
      }, {
        onSuccess: (response) => {
          log.info('sendItems()', 'success', { response_id: response.id });
          // Update the currentCheckRef here to get around a race condition where the
          // necessity to show the check exit prompt was evaluated before the check data update was detected
          checkExitPrompt.store.setCurrentCheckRef(response);
          if (isManualSend) {
            if (terminalSettings.data?.sendItemsAction === 'Back') navigateToDefaultView();
            if (terminalSettings.data?.sendItemsAction === 'Logout') navigate(crumbs.pos.user.logout.interpolate({}));
          }
        },
      });
    } else {
      // This should never be called because the Send button is disabled if no unsent items,
      // but just in case...
      log.info('sendItems()', 'print request not run because there are no unsent items');
    }
  };

  const closeCheck = async () => {
    if (typeof checkId !== 'number') return log.debug('closeCheck', '!checkId');
    if (described.unsent.check.items.length) {
      sendItems();
    }
    updateStatus.mutate({ checkId, status: 'close' }, {
      onSuccess: (response) => {
        toast.showAndAutoHide.warning({
          id: `${sectionId}.checkClosed`,
          title: t('check.labels.checkClosed', { checkLabel: response.label }),
          action: (
            <CTA
              label={t('labels.reprintReceipts')}
              variant="outlined"
              color="inherit"
              onClick={printGuestReceipt}
            />
          ),
        });
        navigateToDefaultView();
      },
      onError: (e) => {
        toast.show.serviceError(e, { id: `${sectionId}.closeCheck` });
      },
    });
  };

  const voidCheck = async () => {
    if (typeof checkId !== 'number') return log.debug('voidCheck', '!checkId');

    await voidCheckForm.action.submitIfValid(async ({ values }) => {
      updateStatus.mutate({ checkId, status: 'void', reasonId: values.reasonId }, {
        onSuccess: (response) => {
          toast.showAndAutoHide.warning({
            id: `${sectionId}.checkVoided`,
            title: t('check.labels.checkVoided', { checkLabel: response.label }),
          });
          modal.voidCheck.close();
          // Update the currentCheckRef here to get around a race condition where the
          // necessity to show the check exit prompt was evaluated before the check data update was detected
          checkExitPrompt.store.setCurrentCheckRef(response);

          navigateToDefaultView({ isFromVoid: true });
        },
        onError: (e) => {
          toast.show.serviceError(e, { id: `${sectionId}.voidCheck` });
          modal.voidCheck.close();
        },
      });
    });
  };

  const onClearCheck = async () => {
    if (typeof checkId !== 'number') return log.debug('onClearCheck', '!checkId');

    if (described.unsent.check.itemIds.length) {
      // need to use mutateAsync here because we can't perform the clear mutation until we remove all check items first
      try {
        await removeCheckItems.mutateAsync({ checkId, itemIds: described.unsent.check.itemIds, reason: 'Clear Check', status: 'remove' });
      } catch (error) {
        log.error(`${sectionId}.onClearCheck`, t('check.errors.removeItem'), { error });
        toast.show.serviceError(error, { id: `${sectionId}.onClearCheck.removeCheckItems` });
        modal.clearCheck.close();
        return;
      }
    }
    clear.mutate({ checkId }, {
      onError: (error) => {
        toast.show.serviceError(error, { id: `${sectionId}.onClearCheck` });
        modal.clearCheck.close();
      },
      onSuccess: (response) => {
        toast.showAndAutoHide.warning({
          id: `${sectionId}.checkCleared`,
          title: t('check.labels.checkCleared', { checkLabel: response.label }),
        });
        modal.clearCheck.close();
        navigateToDefaultView({ isFromClear: true });
      },
    });
  };

  const printGuestReceipt = async () => {
    await printerSend.mutateAsync({ type: 'Guest', checkId });
  };

  const onPayClick = () => {
    currentPayment.setStatus('');
    navigate(crumbs.pos.check.payment.main.interpolate({
      checkId,
      itemSelect: route.params.active.itemSelect,
    }));
  };

  const onSplitClick = () => {
    navigate(crumbs.pos.check.split.main.interpolate({
      checkId,
      itemSelect: route.params.active.itemSelect,
    }));
  };

  const onTransferClick = () => {
    navigate(crumbs.pos.check.transfer.main.interpolate({
      checkId,
      itemSelect: route.params.active.itemSelect,
    }));
  };

  const onPrintClick = () => {
    // Send Guest Receipt Print Request to pos-api for the current check
    // TODO: await the response from pos-api and handle it.
    // Either all validation will pass and a print job will be created,
    // which we don't want to wait for here, or a validation error will be returned.
    // The destination printer will be determined from the terminal session.
    void printerSend.mutateAsync({ type: 'Guest', checkId });
  };

  const canManageDiscounts = useMemo(() => {
    // need to account for old entitlement (pos.checks.discounts.create) until merchants are all converted
    if (grantedPermissions.addDiscounts) return true;
    // permision must be granted
    if (!grantedPermissions.manageDiscounts.isAllowed) return false;
    // discounts must exist on metadata
    if (grantedPermissions.manageDiscounts.metadata?.allowAll || (grantedPermissions.manageDiscounts.metadata?.standardDiscountIds ?? []).length > 0) return true;
    // no discounts exist on metadata
    return false;
  }, [
    grantedPermissions.addDiscounts,
    grantedPermissions.manageDiscounts.isAllowed,
    grantedPermissions.manageDiscounts.metadata?.allowAll,
    grantedPermissions.manageDiscounts.metadata?.standardDiscountIds,
  ]);

  return {
    viewMode: {
      active: viewMode,
      update: viewModeSet,
      is: {
        main: viewMode === undefined || viewMode === 'CheckDetails' || viewMode === 'CheckDetailsExpanded',
        overlay: viewMode === 'CheckItemEdit' || viewMode === 'CheckDetailsMore'
      }
    },
    can: {
      takePayment: grantedPermissions.takePayment,
      manageFees: grantedPermissions.manageFees,
      mergeChecks: grantedPermissions.mergeChecks,
      splitCheck: grantedPermissions.splitCheck,
      transferCheck: grantedPermissions.transferCheck,
      manageDiscounts: canManageDiscounts,
      clearOrVoid: described.can.clear || grantedPermissions.voidCheck,
      quickSend: !!described.unsent.check.itemIds.length && (terminalSettings.data?.sendItemsAction === 'Back' || terminalSettings.data?.sendItemsAction === 'Logout'),
    },
    described,
    modal,
    voidCheckForm,
    voidReasonOptions,
    closeCheck,
    printGuestReceipt,
    onVoidCheck: () => void voidCheck(),
    onClearCheck,
    // button actions
    onSendItemsClick: () => {
      sendItems(true);
    },
    onPayClick,
    onPrintClick,
    onSplitClick,
    onTransferClick,
    isPaymentRoute: route.is(crumbs.pos.check.payment.main),
    isSendingItems: printCheck.isPending,
    sendItems,
  };
};

type UsedCheckPanelFooter = ReturnType<typeof useCheckPanelFooter>;

export { useCheckPanelFooter };
export type { UsedCheckPanelFooter };
